import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

export default function Home() {
  const [isExpanded, setIsExpanded] = useState(false);
  const categories = [
    {
      title: 'Blomsterkort',
      slug: 'alle',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3b41cd86-39ed-45e0-b435-9533dfc4db81.png',
      description: 'Håndtegnede illustrationer og blomsterkort trykt på eksklusivt papir.'
    },
    {
      title: 'Printdesign',
      slug: 'printdesign',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/837dd0c5-9d80-4673-9933-6b9a45f609af.jpg',
      description: 'Specialdesignede printløsninger og illustrationer.'
    },
    {
      title: 'Forskellige reoler',
      slug: 'forskellige-reoler',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/3097ed61-1367-4683-a2b2-c84a1bc49131.png',
      description: 'Håndlavede reoler i resttræ til udstilling af kort.'
    },
    {
      title: 'Aalborg Chokolade',
      slug: 'chokolade',
      image: 'https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/image_1.png',
      description: 'Mørk chokolade med nødder og unikt print. Kun til firmaer.'
    }
  ];

  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Cards by Eve",
    "image": "https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/02690458-fa00-4a74-bd0d-c563c4b7c123.jpg",
    "description": "Håndtegnede illustrationer og lykønskningskort skabt med kærlighed til den nordiske natur og det gode håndværk.",
    "email": "evelyn@cardsbyeve.dk",
    "telephone": "+4531356040",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Højslev",
      "postalCode": "7840",
      "addressCountry": "DK"
    },
    "url": window.location.origin
  };

  return (
    <div className="bg-white">
      <SEO 
        title="Håndtegnede kort & illustrationer"
        description="Oplev unikke håndtegnede illustrationer, lykønskningskort og printdesign fra Cards by Eve. Skabt med kærlighed til den nordiske natur og det gode håndværk."
        schema={homeSchema}
      />
      {/* Hero Section */}
      <section className="relative min-h-[40vh] md:min-h-[55vh] flex items-center overflow-hidden bg-white">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/8e19c858-88a3-49c6-87ac-59ed8ba13870.png"
            alt="Cards by Eve Premium Collection - Håndtegnede lykønskningskort"
            className="w-full h-full object-cover opacity-40"
            decoding="async"
          />
        </div>
        
        {/* Background Gradient Overlay */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#dae6f4] via-[#dae6f4]/50 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 md:py-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl text-brand-950 font-serif leading-tight mb-6 md:mb-10">
              Skønheden i det <br />
              <span className="italic text-brand-600">enkle håndværk</span>
            </h1>
            <p className="text-brand-800 text-base md:text-xl font-light leading-relaxed max-w-xl mb-8 md:mb-12">
              Håndtegnede illustrationer og lykønskningskort, der forener nordisk minimalisme med naturens vilde poesi.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
              <Link
                to="/produkter/blomsterkort"
                className="inline-flex items-center justify-center bg-brand-950 text-white px-8 md:px-10 py-4 md:py-5 text-[10px] md:text-xs uppercase tracking-widest font-medium hover:bg-brand-800 transition-all duration-500 group shadow-lg shadow-brand-950/10"
              >
                Udforsk produkterne
                <ArrowRight className="ml-3 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/kontakt?subject=book-visit"
                className="inline-flex items-center justify-center border border-brand-950 text-brand-950 px-8 md:px-10 py-4 md:py-5 text-[10px] md:text-xs uppercase tracking-widest font-medium hover:bg-brand-950 hover:text-white transition-all duration-500"
              >
                Book et møde
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block">
                  Inspiration
                </span>
                <h2 className="text-5xl md:text-6xl font-serif text-brand-950 leading-tight">
                  Inspireret af naturens <br />
                  <span className="italic">stille øjeblikke</span>
                </h2>
                <p className="text-brand-600 text-lg font-light leading-relaxed">
                  Jeg henter inspiration i naturens ro, i blomsterhandlerens smukke udvalg og i min fantasi. Hver illustration starter som en tanke og en penselstreg hjemme i min stue i Højslev.
                </p>
                <div className="pt-4">
                  <Link to="/hvem-er-jeg" className="inline-flex items-center text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2 hover:text-brand-500 hover:border-brand-500 transition-all">
                    læs historien
                  </Link>
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="aspect-[16/10] overflow-hidden rounded-sm shadow-2xl"
              >
                <img
                  src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/4f4b6a0c-6aaa-4de2-87f0-152849a1ea3a.png"
                  alt="Nærbillede af kunstnerisk proces og detaljeret tegning hos Cards by Eve"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-32 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
            <div className="max-w-2xl">
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block mb-6">
                Se de forskellige produkter
              </span>
              <h2 className="text-5xl md:text-6xl font-serif text-brand-950">
                Find det rette udtryk <br />
                <span className="italic">til enhver anledning</span>
              </h2>
            </div>
            <Link to="/produkter" className="text-brand-950 text-xs uppercase tracking-widest font-bold border-b border-brand-950 pb-2 hover:text-brand-500 hover:border-brand-500 transition-all">
              Produkter
            </Link>
          </div>

          <div className="mb-16 max-w-3xl">
            <p className="text-brand-800 text-lg font-light leading-relaxed">
              Herunder kan du se eksempler på de kategorier og formater, jeg tilbyder. Du kan altid bestille en prøvepakke eller en bestillingsseddel for at se det fulde udvalg.
            </p>
            <div className="mt-8">
              <Link
                to="/produkter/blomsterkort#bestil"
                className="inline-flex items-center justify-center bg-brand-950 text-white px-6 py-4 text-[10px] uppercase tracking-widest font-medium hover:bg-brand-800 transition-all duration-500"
              >
                Bestil prøvepakke eller bestillingsseddel
                <ArrowRight className="ml-3 w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-12">
            <AnimatePresence mode="popLayout">
              {categories.map((cat, index) => (
                <motion.div
                  key={cat.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="group md:col-span-3"
                >
                  <Link to={cat.slug === 'chokolade' ? '/kollektion/chokolade' : `/produkter/${cat.slug}`}>
                    <div className="overflow-hidden rounded-sm mb-8 relative aspect-[3/2]">
                      <img
                        src={cat.image}
                        alt={`Kategori: ${cat.title} - Håndtegnede kort og design fra Cards by Eve`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-brand-950/0 group-hover:bg-brand-950/10 transition-colors duration-500"></div>
                    </div>
                    <h3 className="text-2xl font-serif text-brand-950 mb-3">{cat.title}</h3>
                    <p className="text-brand-500 text-sm font-light leading-relaxed mb-6">{cat.description}</p>
                    <span className="text-brand-950 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center group-hover:translate-x-2 transition-transform duration-500">
                      Udforsk mulighederne <ArrowRight className="ml-2 w-3 h-3" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>


        </div>
      </section>

      {/* Lifestyle Section */}
      <section className="py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div className="order-2 md:hidden lg:block lg:order-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="relative"
              >
                <div className="aspect-[4/5] rounded-sm overflow-hidden shadow-2xl md:max-w-lg md:mx-auto lg:max-w-none">
                  <img
                    src="https://assets.auxodev.dk/assets/19777474-0f63-4a2a-88a2-aa8209d1f0a9/666d7233-381c-4d42-9f79-71540e500510.png"
                    alt="Cards by Eve lykønskningskort vist i et stilfuldt, nordisk hjemmemiljø"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </motion.div>
            </div>
            <div className="order-1 lg:order-2 space-y-10">
              <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium block">
                Kvalitet & Æstetik
              </span>
              <h2 className="text-5xl md:text-6xl font-serif text-brand-950 leading-tight">
                Mere end blot <br />
                <span className="italic">et stykke papir</span>
              </h2>
              <p className="text-brand-600 text-lg font-light leading-relaxed">
                Jeg udvælger nøje de bedste materialer for at sikre, at hvert kort føles lige så eksklusivt, som det ser ud. Fra den taktile tekstur i papiret til de fine nuancer i trykket – jeg går aldrig på kompromis.
              </p>
              <div className="grid grid-cols-2 gap-8 pt-6">
                <div>
                  <h4 className="text-brand-950 font-serif text-xl mb-2">Bæredygtighed</h4>
                  <p className="text-brand-500 text-sm font-light">Jeg bruger FSC-certificeret papir og miljøvenlig produktion.</p>
                </div>
                <div>
                  <h4 className="text-brand-950 font-serif text-xl mb-2">Dansk Design</h4>
                  <p className="text-brand-500 text-sm font-light">Alle illustrationer er skabt og trykt i Danmark.</p>
                </div>
              </div>
              <div className="pt-10 flex flex-wrap gap-4">
                <Link
                  to="/kontakt?subject=book-visit"
                  className="inline-flex items-center justify-center border border-brand-950 text-brand-950 px-8 md:px-10 py-4 md:py-5 text-[10px] md:text-xs uppercase tracking-widest font-medium hover:bg-brand-950 hover:text-white transition-all duration-500"
                >
                  Book et møde
                </Link>
                <Link
                  to="/kontakt?subject=order-samples"
                  className="inline-flex items-center justify-center border border-brand-950 text-brand-950 px-8 md:px-10 py-4 md:py-5 text-[10px] md:text-xs uppercase tracking-widest font-medium hover:bg-brand-950 hover:text-white transition-all duration-500"
                >
                  Bestil prøver
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
